"use client";

import { c as _c } from "react/compiler-runtime";
import * as AlertDialog from "@radix-ui/react-alert-dialog";
import * as Dialog from "@radix-ui/react-dialog";
import * as Popover from "@radix-ui/react-popover";
import { ChevronDownIcon, EllipsisIcon, LoaderIcon, PencilLineIcon, PlusIcon, SparklesIcon, Trash2Icon } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useState } from "react";
import { toast } from "sonner";
import useSWR, { useSWRConfig } from "swr";
import useSWRMutation from "swr/mutation";
import { useToggle } from "usehooks-ts";
import FileUploader from '@/components/form/file-uploader';
import Input from "@/components/input";
import CustomSelect from "@/components/select";
import { Button } from '@/components/ui/button';
import { useAppContext } from "@/context/app";
import { pointOfView, purposeIntent, targetAudience, toneOfVoice, writingStyle } from "@/lib/agent-option";
import { cn, fetcher } from "@/lib/utils";
import ChatHeaderProfileButton from "./chat-header-profile-button";
import tokenIcon from '@/assets/images/token.svg';
import { PAGE_ROUTES } from "@/constants/routes";
function CreateAgentDialog({
  children
}) {
  const [isOpen, setIsOpen] = useState(false);
  const router = useRouter();
  const {
    trigger,
    isMutating,
    data
  } = useSWRMutation("/api/agent", async (key, {
    arg
  }) => {
    const res = await fetch(key, {
      method: "POST",
      body: JSON.stringify(arg)
    });
    const json = await res.json();
    if (!res.ok) return {
      errors: json
    };
    return {
      data: json
    };
  });
  const {
    trigger: triggerFileUpload,
    isMutating: isUploading
  } = useSWRMutation('/api/agent/knowledge', async (key_0, {
    arg: arg_0
  }) => {
    const {
      formData
    } = arg_0;
    try {
      const response = await fetch(key_0, {
        method: 'POST',
        body: formData
      });
      const json_0 = await response.json();
      if (!response.ok) {
        const apiError = typeof json_0.error === 'string' ? json_0.error : 'Failed to upload files.';
        return {
          errors: {
            apiError: [apiError]
          }
        };
      }
      return {
        data: json_0
      };
    } catch (error) {
      console.error("Upload knowledge fetcher exception:", error);
      if (error instanceof Error) {
        return {
          errors: {
            networkError: [error.message || "A network error occurred."]
          }
        };
      }
      return {
        errors: {
          networkError: ["An unknown error occurred."]
        }
      };
    }
  });
  const onSubmit = (agent, formData_0) => {
    toast.promise(trigger(agent, {
      onSuccess: data_0 => {
        formData_0.append('agent_id', data_0.data?.id.toString() ?? '');
        toast.promise(triggerFileUpload({
          formData: formData_0
        }, {
          onSuccess: () => {
            setIsOpen(false);
            router.push(`${PAGE_ROUTES.AGENT}?agentId=${data_0.data?.id}`);
          }
        }), {
          success: "Files uploaded successfully",
          error: "Failed to upload/process files"
        });
      }
    }), {
      success: "Agent created successfully",
      error: "Failed to create agent"
    });
  };
  return <CreateOrUpdateAgentDialog isOpen={isOpen} setIsOpen={setIsOpen} isMutating={isMutating || isUploading} type="create" onSubmit={(agent_0, formData_1) => onSubmit(agent_0, formData_1)} errors={data?.errors}>
      {children}
    </CreateOrUpdateAgentDialog>;
}
function UpdateAgentDialog(t0) {
  const $ = _c(23);
  const {
    children,
    agent
  } = t0;
  const {
    mutate
  } = useSWRConfig();
  const [isOpen, setIsOpen] = useState(false);
  const {
    trigger,
    isMutating,
    data
  } = useSWRMutation(`/api/agent/${agent.id}`, _temp3);
  let t1;
  if ($[0] !== mutate || $[1] !== trigger) {
    t1 = agent_0 => {
      toast.promise(trigger(agent_0, {
        onSuccess: () => {
          mutate("/api/agent");
          setIsOpen(false);
        }
      }), {
        success: "Agent updated successfully",
        error: "Failed to update agent"
      });
    };
    $[0] = mutate;
    $[1] = trigger;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const onSubmit = t1;
  let t2;
  if ($[3] !== onSubmit) {
    t2 = agent_1 => onSubmit(agent_1);
    $[3] = onSubmit;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const t3 = data?.errors;
  const t4 = agent.point_of_view ?? "";
  const t5 = agent.purpose_intent ?? "";
  const t6 = agent.target_audience ?? "";
  const t7 = agent.tone_of_voice ?? "";
  const t8 = agent.writing_style ?? "";
  let t9;
  if ($[5] !== agent.rules) {
    t9 = agent.rules ?? [];
    $[5] = agent.rules;
    $[6] = t9;
  } else {
    t9 = $[6];
  }
  const t10 = agent.status ?? "draft";
  let t11;
  if ($[7] !== agent.name || $[8] !== t10 || $[9] !== t4 || $[10] !== t5 || $[11] !== t6 || $[12] !== t7 || $[13] !== t8 || $[14] !== t9) {
    t11 = {
      name: agent.name,
      point_of_view: t4,
      purpose_intent: t5,
      target_audience: t6,
      tone_of_voice: t7,
      writing_style: t8,
      rules: t9,
      status: t10
    };
    $[7] = agent.name;
    $[8] = t10;
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
    $[12] = t7;
    $[13] = t8;
    $[14] = t9;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  let t12;
  if ($[16] !== children || $[17] !== isMutating || $[18] !== isOpen || $[19] !== t11 || $[20] !== t2 || $[21] !== t3) {
    t12 = <CreateOrUpdateAgentDialog isOpen={isOpen} setIsOpen={setIsOpen} isMutating={isMutating} type="update" onSubmit={t2} errors={t3} initialState={t11}>{children}</CreateOrUpdateAgentDialog>;
    $[16] = children;
    $[17] = isMutating;
    $[18] = isOpen;
    $[19] = t11;
    $[20] = t2;
    $[21] = t3;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  return t12;
}
async function _temp3(key, t0) {
  const {
    arg
  } = t0;
  const res = await fetch(key, {
    method: "PUT",
    body: JSON.stringify(arg)
  });
  const json = await res.json();
  if (!res.ok) {
    return {
      errors: json
    };
  }
  return {
    data: json
  };
}
function CreateOrUpdateAgentDialog(t0) {
  const $ = _c(96);
  const {
    children,
    isOpen,
    setIsOpen,
    type,
    initialState,
    errors,
    isMutating,
    onSubmit
  } = t0;
  const t1 = initialState?.name ?? "";
  const t2 = initialState?.tone_of_voice ?? "";
  const t3 = initialState?.writing_style ?? "";
  const t4 = initialState?.target_audience ?? "";
  const t5 = initialState?.purpose_intent ?? "";
  const t6 = initialState?.point_of_view ?? "";
  let t7;
  if ($[0] !== initialState?.rules) {
    t7 = initialState?.rules ?? [];
    $[0] = initialState?.rules;
    $[1] = t7;
  } else {
    t7 = $[1];
  }
  const t8 = initialState?.status ?? "draft";
  let t9;
  if ($[2] !== initialState?.knowledges || $[3] !== type) {
    t9 = type === "create" && {
      knowledges: initialState?.knowledges
    };
    $[2] = initialState?.knowledges;
    $[3] = type;
    $[4] = t9;
  } else {
    t9 = $[4];
  }
  let t10;
  if ($[5] !== t1 || $[6] !== t2 || $[7] !== t3 || $[8] !== t4 || $[9] !== t5 || $[10] !== t6 || $[11] !== t7 || $[12] !== t8 || $[13] !== t9) {
    t10 = {
      name: t1,
      tone_of_voice: t2,
      writing_style: t3,
      target_audience: t4,
      purpose_intent: t5,
      point_of_view: t6,
      rules: t7,
      status: t8,
      ...t9
    };
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
    $[11] = t7;
    $[12] = t8;
    $[13] = t9;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  const [agent, setAgent] = useState(t10);
  let t11;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = e => {
      setAgent(prev => ({
        ...prev,
        [e.target.name]: e.target.value
      }));
    };
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  const onChange = t11;
  let t12;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = newFiles => {
      setAgent(prev_0 => ({
        ...prev_0,
        knowledges: newFiles
      }));
    };
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  const changeSelectedFiles = t12;
  let t13;
  if ($[17] !== agent || $[18] !== onSubmit) {
    t13 = () => {
      const agentAsFormData = new FormData();
      Object.entries(agent).forEach(t14 => {
        const [key, value] = t14;
        if (Array.isArray(value)) {
          if (key === "knowledges" && value) {
            value.forEach(file => {
              agentAsFormData.append("files", file);
            });
          }
        }
      });
      onSubmit(agent, agentAsFormData);
    };
    $[17] = agent;
    $[18] = onSubmit;
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  const submitHandler = t13;
  let t14;
  if ($[20] !== setIsOpen) {
    t14 = open => setIsOpen(open);
    $[20] = setIsOpen;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  let t15;
  if ($[22] !== children) {
    t15 = <Dialog.Trigger asChild={true}>{children}</Dialog.Trigger>;
    $[22] = children;
    $[23] = t15;
  } else {
    t15 = $[23];
  }
  let t16;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm" />;
    $[24] = t16;
  } else {
    t16 = $[24];
  }
  const t17 = type === "create" ? "Create new agent" : "Update agent";
  let t18;
  if ($[25] !== t17) {
    t18 = <div className="flex flex-col gap-1 px-6"><Dialog.Title className="text-xl capitalize font-semibold">{t17}</Dialog.Title></div>;
    $[25] = t17;
    $[26] = t18;
  } else {
    t18 = $[26];
  }
  let t19;
  if ($[27] !== submitHandler) {
    t19 = e_0 => {
      e_0.preventDefault();
      submitHandler();
    };
    $[27] = submitHandler;
    $[28] = t19;
  } else {
    t19 = $[28];
  }
  const t20 = errors?.name?.[0];
  let t21;
  if ($[29] !== agent.name || $[30] !== t20) {
    t21 = <Input error={t20} label="Name" required={true} name="name" value={agent.name} onChange={onChange} />;
    $[29] = agent.name;
    $[30] = t20;
    $[31] = t21;
  } else {
    t21 = $[31];
  }
  const t22 = errors?.tone_of_voice?.[0];
  const t23 = agent.tone_of_voice;
  let t24;
  let t25;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <option value="" disabled={true}>Select...</option>;
    t25 = toneOfVoice.map(_temp4);
    $[32] = t24;
    $[33] = t25;
  } else {
    t24 = $[32];
    t25 = $[33];
  }
  let t26;
  if ($[34] !== agent.tone_of_voice || $[35] !== t22) {
    t26 = <CustomSelect error={t22} label="Tone of voice" name="tone_of_voice" value={t23} onChange={onChange}>{t24}{t25}</CustomSelect>;
    $[34] = agent.tone_of_voice;
    $[35] = t22;
    $[36] = t26;
  } else {
    t26 = $[36];
  }
  const t27 = errors?.writing_style?.[0];
  const t28 = agent.writing_style;
  let t29;
  let t30;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <option value="" disabled={true}>Select...</option>;
    t30 = writingStyle.map(_temp5);
    $[37] = t29;
    $[38] = t30;
  } else {
    t29 = $[37];
    t30 = $[38];
  }
  let t31;
  if ($[39] !== agent.writing_style || $[40] !== t27) {
    t31 = <CustomSelect error={t27} label="Writing style" name="writing_style" value={t28} onChange={onChange}>{t29}{t30}</CustomSelect>;
    $[39] = agent.writing_style;
    $[40] = t27;
    $[41] = t31;
  } else {
    t31 = $[41];
  }
  const t32 = errors?.target_audience?.[0];
  const t33 = agent.target_audience;
  let t34;
  let t35;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = <option value="" disabled={true}>Select...</option>;
    t35 = targetAudience.map(_temp6);
    $[42] = t34;
    $[43] = t35;
  } else {
    t34 = $[42];
    t35 = $[43];
  }
  let t36;
  if ($[44] !== agent.target_audience || $[45] !== t32) {
    t36 = <CustomSelect error={t32} label="Target audience" name="target_audience" value={t33} onChange={onChange}>{t34}{t35}</CustomSelect>;
    $[44] = agent.target_audience;
    $[45] = t32;
    $[46] = t36;
  } else {
    t36 = $[46];
  }
  const t37 = errors?.purpose_intent?.[0];
  const t38 = agent.purpose_intent;
  let t39;
  let t40;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t39 = <option value="" disabled={true}>Select...</option>;
    t40 = purposeIntent.map(_temp7);
    $[47] = t39;
    $[48] = t40;
  } else {
    t39 = $[47];
    t40 = $[48];
  }
  let t41;
  if ($[49] !== agent.purpose_intent || $[50] !== t37) {
    t41 = <CustomSelect error={t37} label="Puprpose/intent" name="purpose_intent" value={t38} onChange={onChange}>{t39}{t40}</CustomSelect>;
    $[49] = agent.purpose_intent;
    $[50] = t37;
    $[51] = t41;
  } else {
    t41 = $[51];
  }
  const t42 = errors?.point_of_view?.[0];
  const t43 = agent.point_of_view;
  let t44;
  let t45;
  if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
    t44 = <option value="" disabled={true}>Select...</option>;
    t45 = pointOfView.map(_temp8);
    $[52] = t44;
    $[53] = t45;
  } else {
    t44 = $[52];
    t45 = $[53];
  }
  let t46;
  if ($[54] !== agent.point_of_view || $[55] !== t42) {
    t46 = <CustomSelect error={t42} label="Point of view" name="point_of_view" value={t43} onChange={onChange}>{t44}{t45}</CustomSelect>;
    $[54] = agent.point_of_view;
    $[55] = t42;
    $[56] = t46;
  } else {
    t46 = $[56];
  }
  let t47;
  if ($[57] === Symbol.for("react.memo_cache_sentinel")) {
    t47 = <div className={"flex justify-center mt-8 mb-4 relative items-center \n                  before:content before:bg-foreground/20 before:h-[1px] before:w-full\n                  after:content after:bg-foreground/20 after:h-[1px] after:w-full "}><span className="font-semibold px-6 uppercase">Rules</span></div>;
    $[57] = t47;
  } else {
    t47 = $[57];
  }
  let t48;
  if ($[58] !== agent.rules) {
    let t49;
    if ($[60] === Symbol.for("react.memo_cache_sentinel")) {
      t49 = (rule, i_4) => <div key={i_4} className="flex gap-2 items-center"><textarea value={rule} rows={1} className="outline-none border py-2 px-3 rounded-md w-full ring-3 ring-transparent transition border-neutral-300 focus:border-neutral-400 focus:ring-neutral-200" onChange={e_1 => {
          setAgent(prev_1 => {
            const copyRules = [...prev_1.rules];
            copyRules[i_4] = e_1.target.value;
            return {
              ...prev_1,
              rules: copyRules
            };
          });
        }} /><button onClick={() => {
          setAgent(prev_2 => ({
            ...prev_2,
            rules: prev_2.rules.filter((r, idx) => idx !== i_4)
          }));
        }} type="button" className="size-8 flex justify-center items-center cursor-pointer text-foreground/80 hover:text-foreground bg-foreground/5 hover:bg-foreground/10 rounded-lg transition-colors shrink-0"><Trash2Icon className="size-5" /></button></div>;
      $[60] = t49;
    } else {
      t49 = $[60];
    }
    t48 = agent.rules.map(t49);
    $[58] = agent.rules;
    $[59] = t48;
  } else {
    t48 = $[59];
  }
  let t49;
  if ($[61] === Symbol.for("react.memo_cache_sentinel")) {
    t49 = <button type="button" className="flex items-center w-max gap-1 text-foreground/80 cursor-pointer transition-colors hover:text-foreground bg-foreground/5 hover:bg-foreground/10 rounded-full px-2 py-1" onClick={() => {
      setAgent(_temp9);
    }}><PlusIcon className="size-4" /><span>Add rule</span></button>;
    $[61] = t49;
  } else {
    t49 = $[61];
  }
  let t50;
  if ($[62] !== t48) {
    t50 = <div className="w-full">{t47}<div className="w-full flex flex-col gap-2">{t48}{t49}</div></div>;
    $[62] = t48;
    $[63] = t50;
  } else {
    t50 = $[63];
  }
  let t51;
  if ($[64] !== agent.knowledges || $[65] !== isMutating || $[66] !== type) {
    t51 = type === "create" && <div className="flex flex-col mb-8"><span className="font-semibold text-center uppercase pt-6 pb-4">Knowledge Base</span><FileUploader accept={{
        "application/pdf": [".pdf"]
      }} disabled={isMutating} files={agent.knowledges} setFiles={changeSelectedFiles} maxFileCount={5} maxSize={5242880} /></div>;
    $[64] = agent.knowledges;
    $[65] = isMutating;
    $[66] = type;
    $[67] = t51;
  } else {
    t51 = $[67];
  }
  let t52;
  if ($[68] !== t21 || $[69] !== t26 || $[70] !== t31 || $[71] !== t36 || $[72] !== t41 || $[73] !== t46 || $[74] !== t50 || $[75] !== t51) {
    t52 = <div className="w-full px-6 space-y-3 max-h-[calc(100vh-200px)] overflow-y-auto">{t21}{t26}{t31}{t36}{t41}{t46}{t50}{t51}</div>;
    $[68] = t21;
    $[69] = t26;
    $[70] = t31;
    $[71] = t36;
    $[72] = t41;
    $[73] = t46;
    $[74] = t50;
    $[75] = t51;
    $[76] = t52;
  } else {
    t52 = $[76];
  }
  let t53;
  if ($[77] === Symbol.for("react.memo_cache_sentinel")) {
    t53 = <Dialog.Close className="cursor-pointer font-medium border border-neutral-200 hover:bg-neutral-100 rounded-md transition-colors px-6 py-2.5">Cancel</Dialog.Close>;
    $[77] = t53;
  } else {
    t53 = $[77];
  }
  let t54;
  if ($[78] !== isMutating) {
    t54 = isMutating && <LoaderIcon className="size-4 animate-spin " />;
    $[78] = isMutating;
    $[79] = t54;
  } else {
    t54 = $[79];
  }
  let t55;
  if ($[80] !== isMutating || $[81] !== t54 || $[82] !== type) {
    t55 = <div className="flex justify-end gap-2 mt-4 px-6">{t53}<button disabled={isMutating} type="submit" className="cursor-pointer capitalize flex items-center gap-2 disabled:pointer-events-none disabled:opacity-40 font-medium border bg-neutral-900 text-white hover:bg-neutral-800 rounded-md transition-colors px-6 py-2.5">{t54}{type}</button></div>;
    $[80] = isMutating;
    $[81] = t54;
    $[82] = type;
    $[83] = t55;
  } else {
    t55 = $[83];
  }
  let t56;
  if ($[84] !== t19 || $[85] !== t52 || $[86] !== t55) {
    t56 = <form onSubmit={t19} className="w-full">{t52}{t55}</form>;
    $[84] = t19;
    $[85] = t52;
    $[86] = t55;
    $[87] = t56;
  } else {
    t56 = $[87];
  }
  let t57;
  if ($[88] !== t18 || $[89] !== t56) {
    t57 = <Dialog.Portal>{t16}<Dialog.Content aria-describedby="" className="fixed w-full left-1/2 space-y-6 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 py-6 bg-white shadow-lg max-w-xl rounded-lg">{t18}{t56}</Dialog.Content></Dialog.Portal>;
    $[88] = t18;
    $[89] = t56;
    $[90] = t57;
  } else {
    t57 = $[90];
  }
  let t58;
  if ($[91] !== isOpen || $[92] !== t14 || $[93] !== t15 || $[94] !== t57) {
    t58 = <Dialog.Root open={isOpen} onOpenChange={t14}>{t15}{t57}</Dialog.Root>;
    $[91] = isOpen;
    $[92] = t14;
    $[93] = t15;
    $[94] = t57;
    $[95] = t58;
  } else {
    t58 = $[95];
  }
  return t58;
}
function _temp9(prev_3) {
  return {
    ...prev_3,
    rules: prev_3.rules.concat("")
  };
}
function _temp8(option_3, i_3) {
  return <option key={i_3} value={option_3}>{option_3}</option>;
}
function _temp7(option_2, i_2) {
  return <option key={i_2} value={option_2}>{option_2}</option>;
}
function _temp6(option_1, i_1) {
  return <option key={i_1} value={option_1}>{option_1}</option>;
}
function _temp5(option_0, i_0) {
  return <option key={i_0} value={option_0}>{option_0}</option>;
}
function _temp4(option, i) {
  return <option key={i} value={option}>{option}</option>;
}
function AgentListItem(t0) {
  const $ = _c(44);
  const {
    agent,
    mutate,
    closeOption
  } = t0;
  const [agentIdToDelete, setAgentIdToDelete] = useState();
  let t1;
  if ($[0] !== agentIdToDelete || $[1] !== mutate) {
    t1 = () => {
      if (!agentIdToDelete) {
        return;
      }
      const deletePromise = fetch(`/api/agent/${agentIdToDelete}`, {
        method: "DELETE"
      });
      toast.promise(deletePromise, {
        loading: "Deleting agent...",
        success: () => {
          mutate(agents => {
            if (agents) {
              return agents.filter(agent_0 => agent_0.id !== agentIdToDelete);
            }
          });
          setAgentIdToDelete(undefined);
          return "Agent deleted successfully";
        },
        error: "Failed to delete agent"
      });
    };
    $[0] = agentIdToDelete;
    $[1] = mutate;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const handleDeleteAgent = t1;
  let t2;
  if ($[3] !== agent.id) {
    t2 = {
      pathname: PAGE_ROUTES.AGENT,
      query: {
        agentId: agent.id
      }
    };
    $[3] = agent.id;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== agent.name) {
    t3 = agent.name.charAt(0);
    $[5] = agent.name;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t3) {
    t4 = <div className="size-6 shrink-0 rounded-full flex justify-center items-center capitalize transition-colors bg-[#E9E9E9]">{t3}</div>;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== agent.name) {
    t5 = <span className="block truncate">{agent.name}</span>;
    $[9] = agent.name;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== closeOption || $[12] !== t2 || $[13] !== t4 || $[14] !== t5) {
    t6 = <Link onClick={closeOption} href={t2} className="flex w-full items-center gap-2 font-medium cursor-pointer pointer-events-auto group-hover:mask-[linear-gradient(to_right,#000,#000_70%,transparent_82%,transparent_100%)]">{t4}{t5}</Link>;
    $[11] = closeOption;
    $[12] = t2;
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <Popover.Trigger asChild={true}><button type="button" className="absolute right-2 data-[state=open]:opacity-100 data-[state=open]:visible cursor-pointer p-2 invisible group-hover:visible opacity-50 hover:opacity-100"><EllipsisIcon className="size-4" /></button></Popover.Trigger>;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <button type="button" className="cursor-pointer w-full flex items-center gap-2 hover:bg-neutral-100 px-2 py-1.5 rounded-sm transition-colors"><PencilLineIcon className="size-4" /><span>Edit</span></button>;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== agent) {
    t9 = <UpdateAgentDialog agent={agent}>{t8}</UpdateAgentDialog>;
    $[18] = agent;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== agent.id) {
    t10 = () => {
      setAgentIdToDelete(agent.id);
    };
    $[20] = agent.id;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  let t12;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <Trash2Icon className="size-4" />;
    t12 = <span>Delete</span>;
    $[22] = t11;
    $[23] = t12;
  } else {
    t11 = $[22];
    t12 = $[23];
  }
  let t13;
  if ($[24] !== t10) {
    t13 = <button type="button" onClick={t10} className="cursor-pointer w-full flex items-center gap-2 text-red-600 hover:bg-red-100 px-2 py-1.5 rounded-sm transition-colors">{t11}{t12}</button>;
    $[24] = t10;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== t13 || $[27] !== t9) {
    t14 = <Popover.Root>{t7}<Popover.Portal><Popover.Content className="bg-white shadow-xl z-20 border border-neutral-200 p-1 rounded-md min-w-28">{t9}{t13}</Popover.Content></Popover.Portal></Popover.Root>;
    $[26] = t13;
    $[27] = t9;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== t14 || $[30] !== t6) {
    t15 = <div className="relative w-full rounded-full flex group items-center pointer-events-auto  p-1 transition-colors hover:bg-foreground/5">{t6}{t14}</div>;
    $[29] = t14;
    $[30] = t6;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  const t16 = !!agentIdToDelete;
  let t17;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = () => setAgentIdToDelete(undefined);
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  let t18;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <AlertDialog.Overlay className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm" />;
    $[33] = t18;
  } else {
    t18 = $[33];
  }
  let t19;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <div className="flex flex-col gap-2"><AlertDialog.Title className="text-lg font-semibold">Are you absolutely sure?</AlertDialog.Title><AlertDialog.Description className="text-base text-neutral-500">This action cannot be undone. This will permanently delete your chat and remove it from our servers.</AlertDialog.Description></div>;
    $[34] = t19;
  } else {
    t19 = $[34];
  }
  let t20;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <AlertDialog.Cancel className="cursor-pointer font-medium border border-neutral-200 hover:bg-neutral-100 rounded-md transition-colors px-6 py-2.5">Cancel</AlertDialog.Cancel>;
    $[35] = t20;
  } else {
    t20 = $[35];
  }
  let t21;
  if ($[36] !== handleDeleteAgent) {
    t21 = <AlertDialog.Portal>{t18}<AlertDialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 p-6 bg-white shadow-lg max-w-xl rounded-lg">{t19}<div className="flex justify-end gap-2 mt-4">{t20}<AlertDialog.Action onClick={handleDeleteAgent} className="cursor-pointer font-medium border bg-neutral-900 text-white hover:bg-neutral-800 rounded-md transition-colors px-6 py-2.5">Continue</AlertDialog.Action></div></AlertDialog.Content></AlertDialog.Portal>;
    $[36] = handleDeleteAgent;
    $[37] = t21;
  } else {
    t21 = $[37];
  }
  let t22;
  if ($[38] !== t16 || $[39] !== t21) {
    t22 = <AlertDialog.Root open={t16} onOpenChange={t17}>{t21}</AlertDialog.Root>;
    $[38] = t16;
    $[39] = t21;
    $[40] = t22;
  } else {
    t22 = $[40];
  }
  let t23;
  if ($[41] !== t15 || $[42] !== t22) {
    t23 = <>{t15}{t22}</>;
    $[41] = t15;
    $[42] = t22;
    $[43] = t23;
  } else {
    t23 = $[43];
  }
  return t23;
}
function AgentListFloatingOption(t0) {
  const $ = _c(12);
  const {
    agents,
    mutate,
    closeOption
  } = t0;
  const {
    subscription
  } = useAppContext();
  let t1;
  if ($[0] !== agents || $[1] !== closeOption || $[2] !== mutate) {
    t1 = !!agents?.length && <div className="w-full flex flex-col text-left gap-0.5">{agents.map(agent => <AgentListItem key={agent.id} agent={agent} mutate={mutate} closeOption={closeOption} />)}</div>;
    $[0] = agents;
    $[1] = closeOption;
    $[2] = mutate;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="h-[1px] bg-neutral-200 w-full " />;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== agents || $[6] !== subscription.data?.agent_limit || $[7] !== subscription.isLoading) {
    t3 = subscription.isLoading ? <div className="h-8 rounded-full bg-neutral-200 animate-pulse" /> : agents.length >= (subscription.data?.agent_limit ?? 0) ? <Link href="/app/pricing" className="flex pointer-events-auto items-center justify-center py-1.5 cursor-pointer hover:bg-neutral-800 transition-colors gap-2 bg-neutral-900 text-white rounded-full"><SparklesIcon className="size-4" /><span>Upgrade plan</span></Link> : <CreateAgentDialog><button type="button" className="flex pointer-events-auto items-center justify-center py-1.5 cursor-pointer hover:bg-neutral-800 transition-colors gap-2 bg-neutral-900 text-white rounded-full"><PlusIcon className="size-4" /><span>Create new</span></button></CreateAgentDialog>;
    $[5] = agents;
    $[6] = subscription.data?.agent_limit;
    $[7] = subscription.isLoading;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== t1 || $[10] !== t3) {
    t4 = <div className="w-full flex flex-col gap-2 absolute shadow-xl ring-4 ring-[#2B24211A]  top-[calc(100%+8px)] bg-foreground-lightest border border-black/44  rounded-b-xl p-1">{t1}{t2}{t3}</div>;
    $[9] = t1;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  return t4;
}
function AgentList(t0) {
  const $ = _c(39);
  const {
    selectedAgentId
  } = t0;
  const [isOpen, toggleOpen] = useToggle();
  const {
    data: agents,
    mutate,
    isLoading
  } = useSWR("/api/agent", fetcher);
  if (isLoading) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <div className="w-44 bg-foreground/10 animate-pulse rounded-xl h-12.5" />;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  if ($[1] !== agents || $[2] !== isOpen || $[3] !== selectedAgentId || $[4] !== toggleOpen) {
    const selectedAgent = agents?.find(agent => agent.id === selectedAgentId);
    const t7 = !isOpen ? "rounded-xl" : "rounded-t-xl";
    if ($[11] !== t7) {
      t6 = cn("bg-foreground-lightest w-44 ring-4 ring-[#2B24211A] flex-col flex relative", t7);
      $[11] = t7;
      $[12] = t6;
    } else {
      t6 = $[12];
    }
    const t8 = !isOpen ? "rounded-xl" : "rounded-t-xl";
    if ($[13] !== t8) {
      t5 = cn("[background-image:linear-gradient(180deg,rgba(0,0,0,0.22)_50%,rgba(0,0,0,0.44)_100%)] p-[1px]", t8);
      $[13] = t8;
      $[14] = t5;
    } else {
      t5 = $[14];
    }
    t1 = toggleOpen;
    t2 = "button";
    const t9 = !isOpen ? "rounded-xl" : "rounded-t-xl";
    if ($[15] !== t9) {
      t3 = cn("cursor-pointer bg-foreground-lightest w-full flex items-center font-medium gap-3 pointer-events-auto pl-4 pr-2 py-3", t9);
      $[15] = t9;
      $[16] = t3;
    } else {
      t3 = $[16];
    }
    t4 = selectedAgent ? <div className="flex gap-3 items-center grow truncate"><div className="size-6 capitalize flex justify-center items-center bg-[#E9E9E9] shrink-0 rounded-full">{selectedAgent.name.charAt(0)}</div><div className="grow truncate text-left"><span className="block truncate font-medium">{selectedAgent.name}</span></div></div> : <div className="flex gap-3 items-center grow"><div className="size-6 bg-[#E9E9E9] shrink-0 rounded-full" /><div className="grow truncate text-left"><span className="opacity-60">Select agent</span></div></div>;
    $[1] = agents;
    $[2] = isOpen;
    $[3] = selectedAgentId;
    $[4] = toggleOpen;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
  } else {
    t1 = $[5];
    t2 = $[6];
    t3 = $[7];
    t4 = $[8];
    t5 = $[9];
    t6 = $[10];
  }
  const t7 = isOpen && "rotate-180";
  let t8;
  if ($[17] !== t7) {
    t8 = cn("size-4 transition-transform text-foreground/60", t7);
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== t8) {
    t9 = <div className="ml-auto shrink-0 pr-2"><ChevronDownIcon className={t8} /></div>;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== t1 || $[22] !== t2 || $[23] !== t3 || $[24] !== t4 || $[25] !== t9) {
    t10 = <button onClick={t1} type={t2} className={t3}>{t4}{t9}</button>;
    $[21] = t1;
    $[22] = t2;
    $[23] = t3;
    $[24] = t4;
    $[25] = t9;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  let t11;
  if ($[27] !== t10 || $[28] !== t5) {
    t11 = <div className={t5}>{t10}</div>;
    $[27] = t10;
    $[28] = t5;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  let t12;
  if ($[30] !== agents || $[31] !== isOpen || $[32] !== mutate || $[33] !== toggleOpen) {
    t12 = isOpen && <AgentListFloatingOption agents={agents ?? []} mutate={mutate} closeOption={toggleOpen} />;
    $[30] = agents;
    $[31] = isOpen;
    $[32] = mutate;
    $[33] = toggleOpen;
    $[34] = t12;
  } else {
    t12 = $[34];
  }
  let t13;
  if ($[35] !== t11 || $[36] !== t12 || $[37] !== t6) {
    t13 = <div className={t6}>{t11}{t12}</div>;
    $[35] = t11;
    $[36] = t12;
    $[37] = t6;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  return t13;
}
function TokenCounter() {
  const $ = _c(7);
  const {
    subscription: t0
  } = useAppContext();
  const {
    isLoading,
    data
  } = t0;
  if (isLoading) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <div className="animate-pulse h-7 bg-neutral-100 rounded-full w-32" />;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  if (!data) {
    return null;
  }
  const remainingToken = data.remaining_token ?? 0;
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Image src={tokenIcon} alt="token" height={20} width={20} />;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== remainingToken) {
    t2 = new Intl.NumberFormat("en", {
      notation: "compact"
    }).format(remainingToken);
    $[2] = remainingToken;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <PlusIcon />;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t2) {
    t4 = <Button variant="secondary" className="bg-base-white hover:bg-base-white/80">{t1}{t2} Credits{t3}</Button>;
    $[5] = t2;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  return t4;
}
export default function ChatHeader(t0) {
  const $ = _c(5);
  const {
    selectedAgentId
  } = t0;
  let t1;
  if ($[0] !== selectedAgentId) {
    t1 = <div className="flex items-center gap-3"><AgentList selectedAgentId={selectedAgentId} /></div>;
    $[0] = selectedAgentId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="flex items-center justify-end gap-4"><TokenCounter /><ChatHeaderProfileButton /></div>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t1) {
    t3 = <div className="w-full sticky top-0 z-20 p-6 flex justify-between items-center">{t1}{t2}</div>;
    $[3] = t1;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
}